import type { ReportingPlayer } from '@frontend/core/lib/domains/reporting'
import { StaticAssetImage } from '@frontend/react'
import {
  Button,
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
  cn,
  Skeleton,
  type CarouselApi
} from '@frontend/ui'
import { User } from 'lucide-react'
import { useEffect, useState } from 'react'
import { useReportingState } from '../lib/reporting-state'

interface PlayerCarouselProps {
  players: ReportingPlayer[]
  selectedId: string | null
  onSelect: (id: string) => void
  /** Message shown when there are no players to display. */
  emptyMessage?: string
  /** While the session's player set is resolving, show a skeleton instead of
   *  the (stale or not-yet-filtered) roster — a session switch otherwise flashes
   *  the unfiltered list before presence filtering settles. */
  loading?: boolean
}

export function PlayerCarousel({
  players,
  selectedId,
  onSelect,
  emptyMessage = 'No players in session',
  loading = false
}: PlayerCarouselProps) {
  const [api, setApi] = useState<CarouselApi>()
  const [chevronsVisible, setChevronsVisible] = useState(false)
  const { config } = useReportingState()

  // Scroll to selected player when selection changes. Loop mode means the
  // chosen slide can always land in the centre regardless of where it
  // sits in the underlying order.
  useEffect(() => {
    if (!api || !selectedId) return
    const index = players.findIndex((p) => p.id === selectedId)
    if (index >= 0) {
      api.scrollTo(index)
    }
  }, [api, selectedId, players])

  // In loop mode `canScrollPrev/Next` always returns true, so we can't use
  // them to decide whether to show the chevrons. Instead compare the
  // total slide width vs the viewport — if everything fits, hide them.
  useEffect(() => {
    if (!api) return
    const sync = () => {
      const viewportW = api.containerNode().clientWidth
      const contentW = api
        .slideNodes()
        .reduce((sum, n) => sum + n.clientWidth, 0)
      setChevronsVisible(contentW > viewportW + 1)
    }
    sync()
    api.on('reInit', sync)
    return () => {
      api.off('reInit', sync)
    }
  }, [api])

  // Loading wins over the empty state so a session switch shows a skeleton
  // rather than flashing the unfiltered roster (or an empty message) before
  // the all-players summary / roster settles.
  if (loading) {
    return (
      <div className='relative' data-testid='player-carousel-skeleton'>
        <div className='flex justify-center gap-2 px-10 py-2'>
          {Array.from({ length: 6 }).map((_, i) => (
            <div
              key={i}
              className='flex w-[140px] flex-col items-center gap-2 px-2 py-2'
            >
              <Skeleton className='h-20 w-20 rounded-full' />
              <Skeleton className='h-3 w-10' />
              <Skeleton className='h-3 w-24' />
            </div>
          ))}
        </div>
      </div>
    )
  }

  if (players.length === 0) {
    return (
      <div className='flex items-center justify-center py-6'>
        <p className='text-muted-foreground text-sm'>{emptyMessage}</p>
      </div>
    )
  }

  return (
    <div className='relative'>
      <Carousel
        opts={{
          align: 'center',
          // loop lets Embla cycle the slide order so the selected player
          // always lands in the middle — `containScroll` would clamp the
          // ends and prevent the centring, so it's dropped intentionally.
          loop: true,
          duration: 30
        }}
        setApi={setApi}
        className='px-10'
      >
        {/* `justify-center` only applies when the track is shorter than the
            viewport — in loop mode Embla positions slides itself. */}
        <CarouselContent className='-ml-2'>
          {players.map((player) => {
            const isSelected = player.id === selectedId
            const canonical = config.resolvePosition(player.position)
            const posColor = canonical
              ? (config.positionColors[canonical] ?? '#6b7280')
              : '#6b7280'

            return (
              <CarouselItem key={player.id} className='basis-auto pl-2'>
                <Button
                  type='button'
                  variant='ghost'
                  onClick={() => onSelect(player.id)}
                  className={cn(
                    'flex h-auto w-[140px] shrink-0 flex-col items-center gap-2 rounded-lg px-2 py-2 whitespace-normal',
                    isSelected
                      ? 'bg-accent/80 hover:bg-accent/80'
                      : 'hover:bg-accent/50'
                  )}
                >
                  <PlayerAvatar
                    player={player}
                    isSelected={isSelected}
                    posColor={posColor}
                  />
                  <div className='flex flex-col items-center leading-tight'>
                    <span
                      className={cn(
                        'text-xs font-semibold whitespace-nowrap',
                        isSelected ? 'text-foreground' : 'text-muted-foreground'
                      )}
                    >
                      {player.number != null
                        ? `${player.number}`
                        : player.displayName}
                    </span>
                    <span
                      className={cn(
                        'max-w-[128px] truncate text-xs font-medium',
                        isSelected ? 'text-foreground' : 'text-muted-foreground'
                      )}
                    >
                      {player.displayName}
                    </span>
                  </div>
                </Button>
              </CarouselItem>
            )
          })}
        </CarouselContent>
        {chevronsVisible && (
          <>
            <CarouselPrevious
              variant='outline'
              className='top-1/2 left-0 -translate-y-1/2'
            />
            <CarouselNext
              variant='outline'
              className='top-1/2 right-0 -translate-y-1/2'
            />
          </>
        )}
      </Carousel>
    </div>
  )
}

function PlayerAvatar({
  player,
  isSelected,
  posColor
}: {
  player: ReportingPlayer
  isSelected: boolean
  posColor: string
}) {
  const sizeClass = 'h-20 w-20'
  const iconSize = 'h-10 w-10'

  const fallback = (
    <div
      className={cn(
        'flex items-center justify-center rounded-full border-2 transition-all',
        sizeClass,
        isSelected ? 'border-primary' : 'border-transparent'
      )}
      style={{ backgroundColor: `${posColor}20` }}
    >
      <User className={iconSize} style={{ color: posColor }} />
    </div>
  )

  if (player.image) {
    return (
      <StaticAssetImage
        alt={player.displayName}
        src={player.image}
        fallback={fallback}
        className={cn(
          'flex items-center justify-center rounded-full border-2 text-sm font-bold transition-all',
          sizeClass,
          isSelected ? 'border-primary' : 'border-transparent'
        )}
      />
    )
  }

  return fallback
}
